import {Grid} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import _ from 'lodash';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {hasPermission, Permissions, sendUserAudit, user} from '../../../../store/reducers/user';
import API from '../../../../app/api';
import Notes from '../../../../util/notes';
import Formatter from '../../../../util/formatter';
import {setSnackbar} from '../../../../store/reducers/globals';
import MessageBox from '../../../../util/messageBox';
import {getECS} from '../ecs/ecs';
import {additionalData, updatePatientAdditionalData} from '../../../../store/reducers/additionalData';
import {currentMap} from '../../../../store/reducers/ward';
import Menu from '@mui/material/Menu';
import {useTheme} from '@mui/material/styles';
import {patientBanner} from '../../../../controls/agyle/tasks/components/patientBanner';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import * as _find from 'lodash/find';
import {addNoteForPatient} from '../../../../store/reducers/notes';
import {SepsisPathwayStatus} from '../../sepsis/sepsisUtil';
import {ctxPatientLocked, setDischargeTemplate} from '../../../../store/reducers/patientContext';
import DischargeForm from './dischargeForm';
import {getConfig} from '../../../../util/configUtil';
import clsx from 'clsx';
import { AUDIT_TYPES } from '../../../../util/auditUtil';

const Discharge = (props) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const themeStyle = useTheme();

    const patientType = props.patientType;

    // Selectors
    const patientAddData = useSelector(additionalData);
    const isLocked = useSelector(ctxPatientLocked);
    const newCurrentMap = useSelector(currentMap);

    const [dischargeData, setDischargeData] = useState(null);

    // Snackbar
    const [error, setError] = useState(true);
    const [disableDischarge, setDisableDischarge] = useState(false);
    const [isDischargeDisabled, setIsDischargeDisabled] = useState(false);

    // Form check
    const [formDisableDischarge, setFormDisableDischarge] = useState(false);
    const [disableByMidnight, setDisableByMidnight] = useState(false);

    const [fullFormMode, setFullFormMode] = React.useState(props.isActionMode);
    const [openConfirmDeleted, setOpenConfirmDeleted] = React.useState(false);

    // Menus state
    const [menuEl, setMenuEl] = useState(null);
    const open = Boolean(menuEl);
    const [documents, setDocuments] = useState([]);

    // Configs
    const casCardLiteDischargeEnabled = getConfig('casCardLiteDischargeEnabled', false);
    const nursingNotesEnabled = getConfig('nursingNotesEnabled', false);
    const prescriptionEnabled = getConfig('prescriptionEnabled', false);
    const ttaDrugPrescriptionEnabled = getConfig('ttaDrugPrescriptionEnabled', false);
    const postEditDischargeForm = getConfig('postEditDischargeForm', false);
    const showMidnightWarningInDischarge = getConfig('showMidnightWarningInDischarge', false);
    const autoCancelSepsisOnDischarge = getConfig('autoCancelSepsisOnDischarge', false);

    useEffect(() => {
        const doc = [];
        if (casCardLiteDischargeEnabled && !nursingNotesEnabled && !fullFormMode) {
            doc.push({
                text: t('CAS CARD LITE'),
                event: () => createECS('ecsLite'),
            });
        }
        if (nursingNotesEnabled && !fullFormMode) {
            doc.push({
                text: t('NURSING NOTES'),
                event: () => createECS(`nursing.notes${newCurrentMap?.mapPath === 'poole' ? 'PH' : 'RBH'}`),
            });
        }
        if (prescriptionEnabled && !fullFormMode) {
            doc.push({
                text: t('Prescription'),
                event: () => createECS('prescription'),
            });
        }
        if (ttaDrugPrescriptionEnabled && !fullFormMode) {
            doc.push({
                text: t('TTA Drug Pres.'),
                event: () => createECS(`tta.drug.prescription${newCurrentMap?.mapPath === 'poole' ? 'PH' : 'RBH'}`),
            });
        }
        if (postEditDischargeForm &&
            props.patient?.admission?.status === 'DISCHARGE_REQUESTED'
        ) {
            if (!dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_POST_DISCHARGE_FORM))) {
                setIsDischargeDisabled(true);
            } else {
                setIsDischargeDisabled(false);
            }
        }
        setDocuments(doc);
    }, []);

    // Bedas validation check
    useEffect(() => {
        let disabled = false;
        const discharge = _.find(dischargeData, {name: 'studies.discharge'});
        const bedas = _.find(dischargeData, {name: 'bedas.date'});
        if (discharge?.bedas) disabled = _.isNil(bedas?.valueDate);
        setDisableDischarge(disabled);
        if (showMidnightWarningInDischarge) {
            const date = discharge?.value?.departureDateAndTime;
            setDisableByMidnight(moment(date)?.format('HHmm') === '0000');
        }
    }, [dischargeData]);

    const isDischargeEditable = () => postEditDischargeForm && !isDischargeDisabled;

    const validation = () => {
        let isValid = true;
        _.forEach(dischargeData, (data) => {
            if (!isValid) return;

            if (data?.validation) {
                dispatch(setSnackbar({type: 'warning', message: data?.validation}));
                setError(true);
                isValid = false;
            }
        });
        return isValid;
    };
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const incompleteSepsisPath = patientAddData.other?.sepsisPathways ? _find(patientAddData.other.sepsisPathways, ['statusObj', SepsisPathwayStatus.INCOMPLETE]) : null;
    const saveNotes = (sObj) => {
        const note = Notes.getSepsisPathwayNote(sObj);
        dispatch(addNoteForPatient(props.patient?.patient.__id, {
            note: note,
            noteType: 'sepsisPathway',
            patientsInWardsRef: [props.patient.__id],
            referenceId: sObj.id,
            episodeId: props.patient?.admission?.consultantEpisodeId || null,
        }, props.patient.__id));
    };

    const updatePatient = (sObj) => {
        API.updatePatientInWard(props.patient?.patient.__id, [{
            'metadata.sepsisPathway': {
                status: sObj.statusObj,
                neutropenic: sObj.neutropenic,
            },
        }]).then(response => {
            // console.log("Observations.API.updatePatientInWard", response);
        }).catch(error => {
            console.error('Observations.API.updatePatientInWard: ', error);
        });
    };

    const finishPathWay = (reason, sepsisObj) => {
        let index;
        let pathways = [...patientAddData.other?.sepsisPathways];

        index = _.findIndex(pathways, sepsisObj);

        let sObj = {...sepsisObj};

        sObj.statusObj = SepsisPathwayStatus.CANCELED;
        sObj.notRequiredReason = reason;

        if (sObj.statusObj !== SepsisPathwayStatus.INCOMPLETE) {
            sObj.dateEnd = moment().valueOf();
            sObj.userEnd = tester;
            sObj.userEndLogin = userObject.userName;
            sObj.userEndId = userObject.__id;
        }

        if (index > -1) {
            pathways[index] = sObj;
            dispatch(updatePatientAdditionalData(props.patient?.patient.__id, {'sepsisPathways': pathways}));
            updatePatient(sObj);
            saveNotes(sObj);
        }
    };

    const submit = () => {
        if (validation() && dischargeData) {
            if (incompleteSepsisPath && incompleteSepsisPath?.relatedPatientInWardId === props.patient.__id && autoCancelSepsisOnDischarge) {
                finishPathWay('SEPSIS PATHWAY CANCELED by DISCHARGE', incompleteSepsisPath);
            }

            if (props.discharge) {
                props.discharge(dischargeData);
                props.close();
            }

            if (props.onDischargePatient) {
                props.onDischargePatient(dischargeData);
            }

            dispatch(setDischargeTemplate(null));

            if (nursingNotesEnabled) createECS(`nursing.notes${newCurrentMap?.mapPath === 'poole' ? 'PH' : 'RBH'}`);
        } else {
            return false;
        }
    };

    const clearDischarge = () => {
        setOpenConfirmDeleted(true);
    };

    const handleClearDischarge = (e) => {
        API.updatePatientInWard(props.patient.__id, [{
                'studies.discharge': null,
            }])
            .then((response) => {
                if (response.data) {
                    dispatch(setSnackbar({type: 'success', message: 'Discharge timestamp was successfully removed.'}));
                    dispatch(addNoteForPatient(props.patient?.patient.__id, {
                        note: Notes.getDischargeTimestampListNote(null, true),
                        noteType: 'dischargeTimestamp',
                        patientsInWardsRef: [props.patient?.__id],
                        episodeId: props.patient?.admission?.consultantEpisodeId || null,
                    }, props.patient.__id));
                    props.close();
                }
            })
            .catch(e => {
                console.error(e);
                dispatch(setSnackbar({
                    type: 'error',
                    message: 'A problem has occurred when deleting discharge timestamp.',
                }));
            });
    };

    const save = (e) => {
        e.preventDefault();
        const discharge = _.find(dischargeData, {name: 'studies.discharge'});

        if (validTimestamp(discharge?.value)) {
            discharge.value.status = 'timestamp';
            props.onSave([discharge]);
        } else {
            dispatch(setSnackbar({
                type: 'warning',
                message: t('All information required. Please check for correctness.'),
            }));
        }
    };

    const validTimestamp = (data) => {
        const isValid = !_.isNil(data.who)
            && !_.isEmpty(data.departureDate)
            && !_.isEmpty(data.departureTime)
            && !_.isEmpty(data.dischargeStatus)
            && !_.isEmpty(data.dischargeDestination)
            && !_.isEmpty(data.dischargeFollowUp);

        setError(!isValid);
        return isValid;
    };

    const closeDischarge = () => {
        if (props.close) props.close();
    };
    const saveSafeFormStatus = (value) => {
        if (props.onChange) {
            props.onChange(value);
        }
    };

    const createECS = (pdf) => {
      const clone = _.cloneDeep(props.patient);
      if (dischargeData) {
        dischargeData.forEach((value) => {
          _.set(clone, value.name, value.value);
        });
      }

      dispatch(
        sendUserAudit(clone, AUDIT_TYPES.OPEN_DOCUMENT, { pdfTemplate: pdf })
      );

      getECS(clone, patientAddData, patientType, true, pdf).then((res) => {
        console.log(res);
      });
    };

    const handleOpenMenu = (event) => {
        setMenuEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setMenuEl(null);
    };

    const renderMenuSettings = () => {
        return (
            <Grid container direction="column">
                {documents?.map(opt => (
                    <AgyleButton
                        key={opt.text}
                        variant="contained"
                        color="tertiary"
                        style={{margin: '1px 0', color: 'black'}}
                        onClick={() => {
                            opt.event();
                            handleCloseMenu();
                        }}
                    >
                        {opt.text}
                    </AgyleButton>
                ))}
            </Grid>
        );
    };

    const handleOpenFullFormTimestamp = () => {
        if (props.handleOpenFullFormTimestamp) props.handleOpenFullFormTimestamp()
    };

    useEffect(() => {
        const buttons = [];

        if (fullFormMode) {
            buttons.push(
                <AgyleButton
                    variant="contained"
                    color="primary"
                    onClick={handleOpenFullFormTimestamp}
                >
                    {t('Open form')}
                </AgyleButton>,
            );
        }

        if (!props.disableSave && !fullFormMode && props.patient?.studies?.discharge) {
            buttons.push(
                <AgyleButton
                    variant="contained"
                    color="primary"
                    onClick={clearDischarge}
                    disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_DISCHARGE)) || props.patient?.admission?.status === 'DISCHARGE_REQUESTED'}
                >
                    {t('Remove Timestamp')}
                </AgyleButton>,
            );
        }

        if (!_.isEmpty(documents)) {
            buttons.push(
                <AgyleButton
                    variant="contained"
                    color="primary"
                    onClick={handleOpenMenu}
                >
                    {t('Documents')}
                </AgyleButton>,
            );
        }

        if (fullFormMode) {
            buttons.push(
                <AgyleButton
                    variant="contained"
                    color="primary"
                    onClick={save}
                    disabled={props.patient?.admission?.status === 'DISCHARGE_REQUESTED' || disableByMidnight}
                >
                    {t('Submit')}
                </AgyleButton>,
            );
        }

        if (props.agyleDialogInsertButtons) {
            if (((isDischargeEditable()) || (!props.disableSave)) && !fullFormMode) {
                buttons.push(
                    <AgyleButton
                        color="primary"
                        onClick={submit}
                        variant="contained"
                        disabled={(!dispatch(hasPermission(Permissions.AGYLE_CAN_DISCHARGE)) || postEditDischargeForm ? isDischargeDisabled : props.patient?.admission?.status === 'DISCHARGE_REQUESTED') || disableDischarge || formDisableDischarge || isLocked || disableByMidnight}
                    >
                        {t(`${isDischargeEditable() && props?.patient?.studies?.discharge ? 'Edit ' : ''}Discharge`)}
                    </AgyleButton>,
                );
            }

            props.agyleDialogInsertButtons(buttons);
        }

        if (props.onCustomButtons) {
            props.onCustomButtons(buttons);
        }
        if (props.onSubmitFunction) {
            props.onSubmitFunction(submit);
        }
        if (props.onDisableSubmit) {
            props.onDisableSubmit((!dispatch(hasPermission(Permissions.AGYLE_CAN_DISCHARGE)) || postEditDischargeForm ? isDischargeDisabled : props.patient?.admission?.status === 'DISCHARGE_REQUESTED') || disableDischarge || formDisableDischarge || isLocked || disableByMidnight);
        }
        if (props.onCustomSubmitText) {
            props.onCustomSubmitText(`${(isDischargeEditable() && props?.patient?.studies?.discharge) ? 'Edit ' : ''}Discharge`);
        }

    }, [fullFormMode, props.disableSave, props.patient?.studies?.discharge, documents, props.dialogType, props.patient?.admission, dischargeData, isDischargeDisabled, disableDischarge, formDisableDischarge, disableByMidnight]);

    return (
        <>
            <div className={clsx(fullFormMode && 'action-padding')}>
                {fullFormMode && patientBanner(props.patient, true)}

                <DischargeForm
                    patient={props.patient}
                    onChange={setDischargeData}
                    isError={error}
                    dialogType={props?.dialogType}
                    isActionMode={fullFormMode}
                    workflowDisabled={props.workflowDisabled}
                    disableSave={props.disableSave}
                    isDischargeDisabled={isDischargeDisabled}
                    closeDischarge={closeDischarge}
                    saveSafeFormStatus={saveSafeFormStatus}
                    isLocked={isLocked}
                    setFormDisableDischarge={setFormDisableDischarge}
                    _onForceClose={props._onForceClose}
                />
            </div>

            <MessageBox
                title={t('Confirmation')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={handleClearDischarge}
                onClose={() => setOpenConfirmDeleted(false)}
                message={t('Are you sure you want to remove discharge timestamp?')}
                open={openConfirmDeleted}
            />

            <Menu
                anchorEl={menuEl}
                id="buttons-menu"
                open={open}
                onClose={handleCloseMenu}
                PaperProps={{
                    style: {
                        borderRadius: 8,
                        backgroundColor: themeStyle.palette.background.paper,
                        padding: 5,
                    },
                }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            >
                {renderMenuSettings()}
            </Menu>
        </>
    );
};

export default Discharge;